import { useQuery, useMutation, } from "@sveltestack/svelte-query";
import { getProductPrices, getProductWithId } from "./priceCalculator";
import { getFirstPossibleDate } from "./helperMethods";
const DEFAULT_HEADERS = {
    "Content-Type": "application/json",
    "X-Order-Origin": "Skekraft Website",
};
const poll = async ({ fn, validate, interval, maxAttempts, signStatus, }) => {
    let attempts = 0;
    let timeoutId;
    let stopped = false;
    const executePoll = async (resolve, reject) => {
        try {
            const unsubscribe = signStatus.subscribe((status) => {
                if (status === "STOP") {
                    stopped = true;
                    cleanup();
                    return;
                }
            });
            const cleanup = () => {
                unsubscribe();
                clearTimeout(timeoutId);
            };
            if (stopped) {
                cleanup();
                return;
            }
            let result;
            try {
                result = await fn(attempts);
            }
            catch (error) {
                if (stopped)
                    return;
                cleanup();
                return reject({ error, signError: true });
            }
            if (stopped)
                return;
            attempts++;
            if (validate(result)) {
                cleanup();
                return resolve(result);
            }
            else if (maxAttempts && attempts >= maxAttempts) {
                cleanup();
                return reject({ signError: true, message: "Max attempts reached" });
            }
            else {
                timeoutId = setTimeout(executePoll, interval, resolve, reject);
            }
        }
        catch (err) {
            if (stopped)
                return;
            return reject({ err, signError: true });
        }
    };
    return new Promise(executePoll);
};
const api = async (url, init, options) => {
    if (init === undefined) {
        init = {};
    }
    init.headers = Object.assign(DEFAULT_HEADERS, init.headers);
    if (init?.body) {
        init.body = JSON.stringify(init.body);
    }
    return fetch(url, init).then(async (response) => {
        const data = await response.json();
        if ((response.ok || options?.allow[response.status]) &&
            ("success" in data ? data.success : true)) {
            return data;
        }
        else {
            return Promise.reject({ ...data, response_status: response.status });
        }
    });
};
const setProductParameters = (url, data) => {
    for (const prop in data) {
        const value = data[prop];
        if (Array.isArray(value)) {
            value.forEach((v) => {
                url.append(`${prop}[]`, String(v));
            });
        }
        else if (value !== undefined && value !== null) {
            url.append(prop, String(value));
        }
    }
};
const createUrl = (searchParams) => {
    const urlPath = searchParams.charAt(0) === "?" ? searchParams : "?" + searchParams;
    return new URL(urlPath, window.location.origin);
};
export const Products = {
    getProduct: async (id, estimation, params) => {
        const data = await Products.getProducts(params);
        const product = getProductWithId(id, getProductPrices(data.result, estimation));
        if (!product)
            return Promise.reject({
                missing: true,
                response_status: 200,
            });
        return product;
    },
    getProducts: async (params) => {
        if (!params.code) {
            params.code = "default";
        }
        const searchParams = new URLSearchParams({
            "product-api": "postarealookup",
            ...params,
        }).toString();
        return api(createUrl(searchParams), { method: "GET" });
    },
    getInvoiceMethods: async (params) => {
        if (!params.code) {
            params.code = "default";
        }
        const searchParams = new URLSearchParams({
            "product-api": "invoicemethods",
            ...params,
        }).toString();
        return api(createUrl(searchParams), { method: "GET" });
    },
    getProperties: async (params) => {
        if (!params.code) {
            params.code = "default";
        }
        const searchParams = new URLSearchParams({
            "product-api": "Properties",
            ...params,
        }).toString();
        return api(createUrl(searchParams), { method: "GET" });
    },
    pinLookup: async (socialNumber) => {
        const searchParams = new URLSearchParams({
            "product-api": "pinlookup",
            pin: socialNumber.toString(),
        }).toString();
        const data = await api(createUrl(searchParams), { method: "GET" });
        return data;
    },
    signProduct: async ({ data, bankId, signData, signStatus, signWithQRCode, }) => {
        const searchParams = new URLSearchParams({ "product-api": "sign" });
        if (bankId) {
            const status = await Products.signBankId({ data, signData, signStatus, signWithQRCode });
            if (status.result.SignedStatus !== 1 || status.result.ProgressStatusCode !== "COMPLETE") {
                return Promise.reject({ signError: true });
            }
            searchParams.append("bankIdOrderRef", status.result.OrderRef);
        }
        setProductParameters(searchParams, data);
        return api(createUrl(searchParams.toString()), { method: "GET" });
    },
    signBankId: async ({ data, signData, signStatus, signWithQRCode, }) => {
        let useQRCode = true;
        const searchParams = new URLSearchParams({
            "product-api": "signstart",
            attempt: "0",
            signWithQRCode: useQRCode.toString(),
            date: new Date().getTime().toString(),
        });
        setProductParameters(searchParams, data);
        const response = await api(createUrl(searchParams.toString()), {
            method: "GET",
        });
        if (!response?.result?.BankIdOrderRef)
            return Promise.reject({ signError: true });
        let result = response.result;
        signStatus.set("WAITINGFORSIGN");
        signData.subscribe((data) => {
            if (data)
                result = data;
        });
        signData.set(response.result);
        const pollingParams = new URLSearchParams({
            "product-api": "SignStatus",
            BankIdOrderRef: result.BankIdOrderRef,
            BankIdAutoStartToken: result.BankIdAutoStartToken,
            responseDate: result.responseDate,
            signWithQRCode: useQRCode.toString(),
            ref: result.BankIdOrderRef,
        });
        signWithQRCode.subscribe((isQR) => {
            useQRCode = isQR;
            pollingParams.set("signWithQRCode", isQR.toString());
        });
        return await poll({
            fn: (attemt) => {
                pollingParams.set("attempt", attemt.toString());
                return api(createUrl(pollingParams.toString()), { method: "GET" });
            },
            validate: (data) => {
                if (data.result?.base64QRCode) {
                    signData.update((sData) => sData ? { ...sData, base64QRCode: data.result.base64QRCode } : sData);
                }
                return data.result.ProgressStatusCode !== "PENDING" && data.result.SignedStatus !== 0;
            },
            interval: 1500,
            maxAttempts: 30,
            signStatus,
        });
    },
};
const cancelSigning = async (signData) => {
    const cancelParams = new URLSearchParams({
        "product-api": "cancelsign",
        bankIdOrderRef: signData.BankIdOrderRef,
    });
    return await api(createUrl(cancelParams.toString()), { method: "GET" });
};
export const useCancelSigning = (options) => useMutation((signData) => cancelSigning(signData), { ...options });
export const useFetchProduct = (productId, estimation, params, options) => useQuery("updateProducts", () => Products.getProduct(productId, estimation, params), {
    cacheTime: 0,
    refetchOnWindowFocus: false,
    ...options,
});
export const useFetchMicro = (params, options) => useQuery(["microproduktion", params], () => Products.getProducts(params), {
    refetchOnWindowFocus: false,
    cacheTime: 0,
    ...options,
});
export const useGetProducts = (params, options) => useQuery(["products", params], () => Products.getProducts(params), {
    retry: (count, error) => count < 3 && error.response_status != 200,
    ...options,
});
export const useGetInvoiceMethods = (params, options) => useQuery(["invoicemethods", params], () => Products.getInvoiceMethods(params), {
    retry: (count, error) => count < 3 && error.response_status != 200,
    ...options,
});
export const useGetProperties = (params, options) => useQuery(["properties", params], () => Products.getProperties(params), {
    retry: (count, error) => count < 3 && error.response_status != 200,
    ...options,
});
// Power of attorney hides the start date, so a tab left open past midnight would send yesterday's
// earliest date: docs/web-components/browser-gotchas.md
export const refreshAuthorityStartDate = async (client, code, startDate) => {
    const params = { code };
    try {
        const properties = client.getQueryData(["properties", params]) ??
            (await client.fetchQuery(["properties", params], () => Products.getProperties(params)));
        const signDays = properties?.result?.signDays;
        if (signDays === undefined)
            return undefined;
        const firstPossible = getFirstPossibleDate(signDays);
        return !startDate || startDate < firstPossible ? firstPossible : undefined;
    }
    catch {
        return undefined;
    }
};
export const useCheckPurchase = (params, options) => useQuery(["checkPurchase", params], () => {
    const searchParams = new URLSearchParams({
        "product-api": "CheckPurchase",
        ...params,
    }).toString();
    return api(createUrl(searchParams), { method: "GET" });
}, {
    ...options,
});
